/* MS Project XML, the format File > Save As > XML produces.
 *
 * It is called MSPDI and it is plain XML, which is the whole reason this
 * exists: it parses in the browser for nothing, where the .mpp binary would
 * need a Java library on a server we would have to pay for.
 *
 * Nothing here is specific to any one plan. It was written against the WAPA
 * integrated plan and checked row by row against the same file read by MPXJ,
 * which is a different implementation by different people, so a mistake in
 * this one would have shown up as a disagreement.
 */

const NS = "http://schemas.microsoft.com/project";

/* MS Project numbers its four relationship types, and the numbering is not
   the order anybody would guess: finish to start, the ordinary one, is 1 and
   not 0. The WAPA plan uses three of the four. */
const LINK_TYPES = { 0: "FF", 1: "FS", 2: "SF", 3: "SS" };

const text = (el, tag) => {
  if (!el) return null;
  const found = el.getElementsByTagNameNS(NS, tag)[0]
    ?? el.getElementsByTagName(tag)[0];
  /* getElementsByTagName searches descendants, and a Task's own children are
     the only ones that count. A Task never nests a Task in this format, so
     the first hit is always the right one, but the guard is cheap. */
  if (!found || found.parentNode !== el) return null;
  const v = found.textContent;
  return v === "" ? null : v;
};

/* MSPDI writes dates as 2025-08-29T09:00:00 in the plan's own local time.
   The date is the whole meaning here, so the clock is dropped rather than
   converted: a task that finishes on the 29th finishes on the 29th in the
   territory and in the office. */
const asDate = (v) => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v ?? ""));
  return m ? m[1] : null;
};

const asNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isTrue = (v) => v === "1" || v === "true";

export const looksLikeMsProjectXml = (xmlText) =>
  /<Project[\s>][^>]*schemas\.microsoft\.com\/project/i.test(xmlText)
  || /<Project\s+xmlns=["']http:\/\/schemas\.microsoft\.com\/project/i.test(xmlText);

/* Returns the shape the schedule register wants, plus enough context for the
   preview to say what is about to happen. Throws with a sentence a project
   manager can act on rather than a parser error. */
export function parseMsProjectXml(xmlText) {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");

  const failure = doc.getElementsByTagName("parsererror")[0];
  if (failure) {
    throw new Error(
      "This file is not readable as XML. If it came out of MS Project, use " +
      "File then Save As and choose XML rather than renaming the .mpp.",
    );
  }

  const root = doc.documentElement;
  if (!root || root.localName !== "Project") {
    throw new Error(
      "This XML is not an MS Project plan. Its outermost tag is " +
      `${root ? root.localName : "missing"} rather than Project.`,
    );
  }

  const taskNodes = [...(root.getElementsByTagNameNS(NS, "Task").length
    ? root.getElementsByTagNameNS(NS, "Task")
    : root.getElementsByTagName("Task"))];

  const activities = [];
  const links = [];
  const skipped = { blank: 0, unnamed: 0 };

  for (const t of taskNodes) {
    /* MS Project keeps blank rows in the file as IsNull. They are spacing in
       somebody's view, not work. */
    if (isTrue(text(t, "IsNull"))) { skipped.blank += 1; continue; }

    const name = (text(t, "Name") ?? "").trim();
    if (!name) { skipped.unnamed += 1; continue; }

    const actualStart = asDate(text(t, "ActualStart"));
    const actualFinish = asDate(text(t, "ActualFinish"));

    /* The UID is how the file refers to a task from anywhere else in itself,
       and it is the only thing a predecessor link points at. Not kept beyond
       the import: the app issues its own code, because MS Project's UIDs are
       reused across files and are not stable across a rebaseline. */
    const uid = (text(t, "UID") ?? "").trim();

    /* Lag arrives in tenths of a minute, which is a real unit MS Project
       really uses. Every link in both WAPA files is zero, but a plan with a
       genuine two week lag would otherwise import as 201600 days. */
    for (const link of [...t.getElementsByTagName("PredecessorLink")]) {
      const from = (text(link, "PredecessorUID") ?? "").trim();
      if (!from) continue;
      const tenths = asNumber(text(link, "LinkLag")) ?? 0;
      links.push({
        from,
        to: uid,
        link_type: LINK_TYPES[text(link, "Type") ?? "1"] ?? "FS",
        lag_days: Math.round(tenths / 4800),
      });
    }

    activities.push({
      uid,
      title: name,
      /* WBS is what links a row to its parent, and it is what the plan calls
         the activity in a meeting. */
      activity_id: (text(t, "WBS") ?? text(t, "OutlineNumber") ?? "").trim() || null,
      outline_level: asNumber(text(t, "OutlineLevel")) ?? 1,
      /* An actual date beats a scheduled one. 267 activities in the WAPA plan
         are finished, and writing the scheduled dates over them would turn
         finished work back into forecast. */
      start_date: actualStart ?? asDate(text(t, "Start")),
      finish_date: actualFinish ?? asDate(text(t, "Finish")),
      start_is_actual: !!actualStart,
      finish_is_actual: !!actualFinish,
      percent_complete: asNumber(text(t, "PercentComplete")),
      notes: (text(t, "Notes") ?? "").trim() || null,
      is_milestone: isTrue(text(t, "Milestone")),
      is_summary: isTrue(text(t, "Summary")),
    });
  }

  if (!activities.length) {
    throw new Error("This plan has no tasks in it. Nothing to import.");
  }

  const dates = activities
    .flatMap((a) => [a.start_date, a.finish_date])
    .filter(Boolean)
    .sort();

  /* A link whose other end was skipped as a blank row points at nothing. */
  const known = new Set(activities.map((a) => a.uid).filter(Boolean));
  const usable = links.filter((l) => known.has(l.from) && known.has(l.to));

  return {
    format: "MS Project XML",
    projectName: (text(root, "Title") || text(root, "Name") || "").trim() || null,
    /* Both WAPA files leave this unset, which is why neither can report
       progress against time. Read so the app can say so rather than guess. */
    statusDate: asDate(text(root, "StatusDate")),
    activities,
    links: usable,
    danglingLinks: links.length - usable.length,
    skipped,
    milestoneCount: activities.filter((a) => a.is_milestone).length,
    finishedCount: activities.filter((a) => a.finish_is_actual).length,
    start: dates[0] ?? null,
    finish: dates[dates.length - 1] ?? null,
  };
}

/* The workstream an activity belongs to, taken from the nearest ancestor whose
   name ends in Workstream. Both real plans group that way inside each stage,
   and workstream is the field every other register in this app is keyed on, so
   carrying it across is what lets a schedule row sit beside a risk. */
export function inferWorkstreams(activities) {
  const byWbs = new Map();
  for (const a of activities) if (a.activity_id) byWbs.set(a.activity_id, a);

  return activities.map((a) => {
    let wbs = a.activity_id ?? "";
    while (wbs.includes(".")) {
      wbs = wbs.slice(0, wbs.lastIndexOf("."));
      const ancestor = byWbs.get(wbs);
      if (ancestor && /workstream$/i.test(ancestor.title.trim())) {
        return { ...a, workstream: ancestor.title.trim() };
      }
    }
    return { ...a, workstream: null };
  });
}
